import { useState } from 'react';
import { useRecoilState } from 'recoil';
import { ChevronUp, ChevronDown } from 'lucide-react';

import { logoTransformState } from 'state/langState';

import { XYControls } from './controls/XYControls';
import { DepthControls } from './controls/DepthControls';
import ScaleSlider from './controls/ScaleSlider';

import { getSafeTransform } from 'utils/getText';

export default function LogoController({ product }) {
  const [transforms, setTransforms] = useRecoilState(logoTransformState);

  const transform = getSafeTransform(transforms, product);

  const [collapsed, setCollapsed] = useState(true);

  /* ===============================
     UPDATE
  =============================== */

  function updateTransform(patch) {
    setTransforms((prev) => {
      const current = getSafeTransform(prev, product);

      return {
        ...prev,
        [product]: {
          ...current,
          ...patch,
          position: {
            ...current.position,
            ...(patch.position ?? {}),
          },
        },
      };
    });
  }

  function setPosition(position) {
    updateTransform({ position });
  }

  function setDepth(value) {
    updateTransform({ depth: value });
  }

  function setScale(value) {
    updateTransform({ scale: value });
  }

  function reset() {
    updateTransform({
      position: {
        x: 0,
        y: 0.6,
      },
      depth: 0.02,
      scale: 0.8,
    });
  }

  /* ===============================
     UI
  =============================== */

  return (
    <div
      className='
        absolute

        left-1/2
        -translate-x-1/2

        bottom-4
        w-[92%]
        max-w-[560px]

        lg:left-auto
        lg:translate-x-0
        lg:right-6
        lg:w-[240px]

        bg-white
        border
        border-[#E7E2D9]
        rounded-2xl
        shadow-lg

        px-4
        py-3

        lg:px-5
        lg:py-5

        flex
        flex-col

        backdrop-blur

        transition-all
        duration-300

        pb-[calc(env(safe-area-inset-bottom)+12px)]
      '
    >
      {/* HEADER */}

      <div className='flex items-center justify-between'>
        <span
          className='
            text-xs
            font-semibold
            tracking-wide
            text-[#1F1F1F]
          '
        >
          Logo Controls
        </span>

        <div className='flex items-center gap-2'>
          <button
            onClick={reset}
            className='
              text-[11px]
              text-[#8A867F]
              hover:text-[#C6A75E]
              transition
            '
          >
            Reset
          </button>

          {/* collapse button */}

          <button
            onClick={() => setCollapsed((v) => !v)}
            className='
              flex
              items-center
              justify-center

              w-7
              h-7

              rounded-lg

              border
              border-[#E7E2D9]

              hover:bg-[#F3F1EA]

              transition
            '
          >
            {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* divider */}

      <div className='h-px bg-[#E7E2D9] mt-3' />

      {/* COLLAPSIBLE AREA */}

      <div
        className={`
          overflow-hidden
          transition-all
          duration-300
          ease-out

          ${collapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100 mt-4'}

          lg:max-h-none
          lg:opacity-100
          lg:mt-4
        `}
      >
        <div className='flex flex-col gap-4'>
          <XYControls
            transform={transform}
            setPosition={setPosition}
            product={product}
          />

          <DepthControls depth={transform.depth} setDepth={setDepth} />

          <ScaleSlider scale={transform.scale} setScale={setScale} />
        </div>
      </div>
    </div>
  );
}
